import React, { FC, useCallback } from 'react'

import { useMutation } from '@apollo/client'
import clsx from 'clsx'

import { UPDATE_CART_ITEM, DESTROY_CART_ITEM } from 'Const'
import useToggle from 'Hooks/useToggle'
import { CartItem as CartItemType } from 'Types'
import { stopPropagation } from 'Utils'

import BtnQtyDown from './BtnQtyDown'
import BtnQtyUp from './BtnQtyUp'

const CartItem: FC<CartItemType> = ({
  id,
  name,
  variantName,
  quantity,
  recipient,
  customerNote,
  totalPriceFormatted,
  cartItemModifiers,
  type,
}) => {
  const [isShowControls, , shopControls] = useToggle()

  const [update, { loading: updating }] = useMutation(UPDATE_CART_ITEM)
  const [destroy, { loading: destroying }] = useMutation(DESTROY_CART_ITEM)

  const handleChange = useCallback(
    (up = true) => {
      if (up) {
        void update({ variables: { id, quantity: quantity + 1 } })
      } else {
        void update({ variables: { id, quantity: quantity - 1 || 1 } })
      }
    },
    [update, id, quantity],
  )

  const handleDestroy = useCallback(() => {
    void destroy({ variables: { id } })
  }, [destroy, id])

  return (
    <div className="checkout-order__product / checkout-item">
      <div className="checkout-item__inner">
        <div className="checkout-item__info">
          <b>{name}</b>
          {variantName ? (
            <span>
              {quantity} x {variantName}
            </span>
          ) : null}
          <span>{recipient?.nickname}</span>

          {customerNote ? (
            <div className="shop-cart__product-customer-note d-flex">
              &#40;
              <div className="shop-cart__product-customer-note__nowrap">{customerNote}</div>
              &#41;
            </div>
          ) : null}
        </div>

        <div className="checkout-item__price">
          <span>{totalPriceFormatted}</span>

          <button className="checkout-item__edit" type="button" onClick={shopControls}>
            <svg
              className="sociavore-icon"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>
        </div>
      </div>

      {cartItemModifiers.length ? (
        <div className="checkout-item__details">
          {cartItemModifiers.map((cartItemModifier) => (
            <p key={cartItemModifier.id}>
              + {cartItemModifier.modifier.name}
              {cartItemModifier.quantity > 1 ? `(x${cartItemModifier.quantity})` : null} {cartItemModifier.totalPriceFormatted}
            </p>
          ))}
        </div>
      ) : null}

      <div
        className={clsx('checkout-item__controls', { 'checkout-item__controls_loading': updating || destroying })}
        hidden={!isShowControls}
      >
        <div className="checkout-item__amount">
          {type !== 'digital_gift_cardable' ? (
            <div className="cart-quantity / quantity--sm / position-relative">
              <label className="sr-only" htmlFor={`product-amount-${id}`}>
                Amount
              </label>
              <input
                required
                className="form-control / mb-0"
                id={`product-amount-${id}`}
                placeholder="Quantity"
                value={quantity}
                onChange={stopPropagation}
              />

              <div className="quantity-nav">
                <BtnQtyUp onChange={handleChange} />
                <BtnQtyDown disabled={quantity === 1} onChange={handleChange} />
              </div>
            </div>
          ) : null}
        </div>

        <div className="checkout-item__remove">
          <button className="btn" onClick={handleDestroy}>
            Remove
          </button>
        </div>
      </div>
    </div>
  )
}

export default CartItem
